import React from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import PublicProfile from '../components/PublicProfile';
import { CashbookView } from '../components/CashbookView';
import HistoryPage from '../components/HistoryPage';
import { Profile } from '../components/Profile';
import InventoryPage from '../components/InventoryPage';
import { TrashcanView } from '../components/TrashcanView';
import SearchView from '../components/SearchView';
import { CrewManager } from '../components/CrewManager';
import { PfandspendeForm } from '../components/PfandspendeForm';
import QuickMarkPage from '../components/QuickMarkPage';
import BarcodeScanner from '../components/BarcodeScanner';
import { StepTracker } from '../components/StepTracker';
import { Overview } from '../components/Overview';
import AnnouncementCenterPage from '../components/announcements/AnnouncementCenterPage';
import LeaderboardPage from '../components/LeaderboardPage';
import DeliveryStoresPage from '../components/DeliveryStoresPage';
import ExpeditionsPage from '../components/ExpeditionsPage';
import FreibeuterhafenPage from '../components/FreibeuterhafenPage';
import FundWorkshopPage from '../components/FundWorkshopPage';
import TavernPage from '../components/TavernPage';
import ShipyardPage from '../components/ShipyardPage';
import HarborMasterPage from '../components/HarborMasterPage';
import HarborActiveOrderTask from '../components/HarborActiveOrderTask';
import HarborOrderCta from '../components/HarborOrderCta';
import PiratenpostPage from '../components/PiratenpostPage';
import HelpCenterPage from '../components/HelpCenterPage';
import CollectionPage from '../components/collection/CollectionPage';
import EventChallengeBanner from '../components/events/EventChallengeBanner';
import EventChallengePage from '../components/events/EventChallengePage';
import '../styles/pirate-pages.css';
import MapViewSwitch from '../components/map-view/MapViewSwitch';
import { MAP_VIEW_3D, MAP_VIEW_CLASSIC, useMapView } from '../map-view/MapViewContext';
import { supportsWebGl } from '../map-view/mapViewState.mjs';
import EncounterOverlay from '../encounters/EncounterOverlay';

const GameMap3D = React.lazy(() => import(/* webpackChunkName: "game-map-3d" */ '../components/game-map/GameMap3D'));

const PirateShipGame = React.lazy(() => import(
  /* webpackChunkName: "pirate-ship-game" */ '../game/PirateShipGame'
));
const TreasureIslandGame = React.lazy(() => import(
  /* webpackChunkName: "treasure-island-game" */ '../game/treasureIsland/TreasureIslandGame'
));

function ShipGameRoute() {
  return (
    <React.Suspense
      fallback={
        <div className="pfp-ship-route-loading" role="status">
          <strong>Das Schiff wird klargemacht ...</strong>
        </div>
      }
    >
      <PirateShipGame />
    </React.Suspense>
  );
}

function TreasureIslandRoute() {
  return (
    <React.Suspense fallback={<div className="pfp-ship-route-loading" role="status"><strong>Die Schatzinsel wird vorbereitet ...</strong></div>}>
      <TreasureIslandGame />
    </React.Suspense>
  );
}

function Clock() {
  const [now, setNow] = React.useState(new Date());

  React.useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return <div className="pfp-overview-clock">{now.toLocaleString()}</div>;
}

function OverviewPage({ entries, user, rotation, add, giveXP, setXpUp, freeValue, setFreeValue }) {
  return (
    <div className="pfp-overview-page">
      <MapViewSwitch variant="classic" />
      <EventChallengeBanner />
      <StepTracker rotation={rotation} />
      <Clock />
      <Overview
        add={add}
        giveXP={giveXP}
        user={user}
        freeValue={freeValue}
        setFreeValue={setFreeValue}
        setXpUp={setXpUp}
        entries={entries}
      />
      <a href="#/quick-mark" className="pfand-button pfand-button--order pfp-overview-quick-mark-button">
        📍 Pfand für Sammler markieren
      </a>
      <a href="#/expeditions" className="pfp-expedition-overview-card">
        <span className="pfp-expedition-overview-icon">🧭</span>
        <span>
          <strong>Pfandpirat Expeditionen</strong>
          <em>Virtuelle Schätze entdecken, Kartenfragmente sammeln und echte Pfandfunde aufwerten.</em>
        </span>
      </a>
      <a href="#/ship" className="pfp-ship-overview-card">
        <span className="pfp-ship-overview-icon" aria-hidden="true">⛵</span>
        <span>
          <strong>Schatzkurs setzen</strong>
          <em>Steuere dein Piratenschiff durch Wind und Wellen bis zur Schatzinsel.</em>
        </span>
        <span className="pfp-ship-overview-action" aria-hidden="true">Ablegen</span>
      </a>
      <HarborActiveOrderTask
        entries={entries}
        fallback={<HarborOrderCta />}
      />
    </div>
  );
}

class Map3dErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.warn('[Pfandpirat] Die 3D-Spielkarte konnte nicht geladen werden.', error);
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

function MapViewFallback({ message }) {
  const { setView } = useMapView();
  return (
    <section className="pfp-map-view-fallback" role="alert">
      <MapViewSwitch variant="fallback" />
      <h2>3D-Spielkarte nicht verfügbar</h2>
      <p>{message}</p>
      <button type="button" className="pfp-map-view-fallback__action" onClick={() => setView(MAP_VIEW_CLASSIC)}>
        Klassische Ansicht weiterverwenden
      </button>
    </section>
  );
}

function MapViewLoading() {
  return (
    <section className="pfp-map-view-loading" role="status" aria-live="polite">
      <MapViewSwitch variant="loading" />
      <strong>3D-Spielkarte wird geladen …</strong>
      <p>MapLibre und die Spielwelt werden erst jetzt nach deiner Auswahl geladen.</p>
    </section>
  );
}

function OverviewModePage(props) {
  const { view, dataSaverEnabled } = useMapView();
  const [fatalError, setFatalError] = React.useState('');

  React.useEffect(() => {
    if (view !== MAP_VIEW_3D) setFatalError('');
  }, [view]);

  if (view !== MAP_VIEW_3D) return <><OverviewPage {...props} /><EncounterOverlay /></>;

  const fallbackMessage = fatalError || 'Die 3D-Karte kann auf diesem Gerät nicht zuverlässig dargestellt werden. Du kannst die klassische Ansicht weiterverwenden.';
  if (fatalError) return <><MapViewFallback message={fallbackMessage} /><EncounterOverlay /></>;
  if (!supportsWebGl()) return <><MapViewFallback message={fallbackMessage} /><EncounterOverlay /></>;

  return (
    <>
      <Map3dErrorBoundary fallback={<MapViewFallback message={fallbackMessage} />}>
        <React.Suspense fallback={<MapViewLoading />}>
          <GameMap3D
            user={props.user}
            entries={props.entries}
            add={props.add}
            loadEntries={props.loadEntries}
            setXpUp={props.setXpUp}
            reducedDataMode={dataSaverEnabled}
            onFatalError={(error) => setFatalError(error?.message || fallbackMessage)}
          />
        </React.Suspense>
      </Map3dErrorBoundary>
      <EncounterOverlay />
    </>
  );
}

function PublicProfileWrapper() {
  const { username } = useParams();
  return <PublicProfile username={username} />;
}

function CashbookWrapper() {
  const { crewId } = useParams();
  return <CashbookView crewId={crewId} />;
}

function QuickMarkRoute() {
  const location = useLocation();
  return <QuickMarkPage source="app" selectedDrink={location.state?.drink || null} />;
}

export function AppRoutes({
  entries,
  user,
  rotation,
  add,
  giveXPBonus,
  setXpUp,
  freeValue,
  setFreeValue,
  displayEntries,
  update,
  handleRemove,
  userColors,
  filter,
  setFilter,
  customRange,
  setCustomRange,
  load,
  applyReceiptRedemptionResult,
  crews,
  crewMembers,
  crewId,
  setCrewId,
  create,
  join,
  leave,
  reload,
  crewEntries,
  setCbCrew,
  loadMembers,
  cbCrew,
  drinksApiUrl,
  setShowScanner,
  barcode,
  setBarcode,
  announcements,
  unreadCount,
  announcementsLoading,
  announcementsError,
  markAnnouncementRead,
}) {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/overview" replace />} />
      <Route
        path="/overview"
        element={
          <OverviewModePage
            entries={entries}
            user={user}
            rotation={rotation}
            add={add}
            giveXP={giveXPBonus}
            setXpUp={setXpUp}
            freeValue={freeValue}
            setFreeValue={setFreeValue}
            loadEntries={load}
          />
        }
      />
      <Route
        path="/news"
        element={
          <AnnouncementCenterPage
            announcements={announcements}
            loading={announcementsLoading}
            error={announcementsError}
            onRead={markAnnouncementRead}
          />
        }
      />
      <Route path="/hilfe" element={<HelpCenterPage />} />
      <Route path="/help" element={<HelpCenterPage />} />
      <Route path="/getraenke-liefern" element={<DeliveryStoresPage />} />
      <Route path="/lieferando" element={<DeliveryStoresPage />} />
      <Route path="/stores" element={<DeliveryStoresPage />} />
      <Route path="/expeditions" element={<ExpeditionsPage />} />
      <Route path="/ship" element={<ShipGameRoute />} />
      <Route path="/treasure-island" element={<TreasureIslandRoute />} />
      <Route path="/events" element={<EventChallengePage />} />
      <Route path="/events/:eventIdentifier" element={<EventChallengePage />} />
      <Route path="/piratenpost" element={<PiratenpostPage />} />
      <Route path="/collection" element={<CollectionPage add={add} user={user} />} />
      <Route
        path="/history"
        element={
          <HistoryPage
            displayEntries={displayEntries}
            safeUpdate={update}
            handleRemove={handleRemove}
            userColors={userColors}
            filter={filter}
            setFilter={setFilter}
            customRange={customRange}
            setCustomRange={setCustomRange}
            reloadEntries={load}
            applyReceiptRedemptionResult={applyReceiptRedemptionResult}
          />
        }
      />
      <Route
        path="/history1"
        element={
          <HistoryPage
            displayEntries={displayEntries}
            safeUpdate={update}
            handleRemove={handleRemove}
            userColors={userColors}
            filter={filter}
            setFilter={setFilter}
            customRange={customRange}
            setCustomRange={setCustomRange}
            reloadEntries={load}
            applyReceiptRedemptionResult={applyReceiptRedemptionResult}
          />
        }
      />
      <Route
        path="/profile"
        element={
          <Profile
            user={user}
            entries={entries}
            onUpdate={load}
            applyReceiptRedemptionResult={applyReceiptRedemptionResult}
            onRedeem={(amount, chestType) => {
              console.log(`Redeem ausgelöst: ${Number(amount).toFixed(2)}€ – Truhentyp: ${chestType}`);
            }}
          />
        }
      />
      <Route path="/inventar" element={<InventoryPage />} />
      <Route path="/freibeuterhafen" element={<FreibeuterhafenPage />} />
      <Route path="/freibeuterhafen/fundwerkstatt" element={<FundWorkshopPage />} />
      <Route path="/freibeuterhafen/hafenmeister" element={<HarborMasterPage />} />
      <Route path="/freibeuterhafen/schiffsduelle" element={<HarborMasterPage />} />
      <Route path="/freibeuterhafen/taverne" element={<TavernPage />} />
      <Route path="/freibeuterhafen/werft" element={<ShipyardPage />} />
      <Route path="/kampfdeck" element={<HarborMasterPage />} />
      <Route path="/shipyard" element={<ShipyardPage />} />
      <Route path="/map" element={<TrashcanView />} />
      <Route
        path="/search"
        element={
          <SearchView
            apiUrl={drinksApiUrl}
            canManageDrinks={window?.pfandpiratData?.canManageDrinks}
            onAdd={(amount, drinkObj) => {
              const meta = {
                barcode: drinkObj.ean_code,
                product_name: drinkObj.product_name,
                volume: drinkObj.volume,
                description: drinkObj.description,
                pfand_info: drinkObj.pfand_info,
                quantity: drinkObj.quantity || 1,
                package_type: drinkObj.package_type || 'single',
                package_label: drinkObj.package_label || '',
                package_units: drinkObj.package_units || 1,
                unit_amount: drinkObj.unit_amount || amount,
                package_deposit: drinkObj.package_deposit || amount,
                activity_source: drinkObj.source || 'search',
                source_id: drinkObj.source_id || drinkObj.id || 0,
                internal_url: drinkObj.internal_url || '',
                external_url: drinkObj.external_url || '',
              };
              return add(amount, meta, user?.id);
            }}
            onScanClick={() => setShowScanner(true)}
            barcode={barcode}
          />
        }
      />

      <Route
        path="/leaderboard"
        element={
          <LeaderboardPage
            user={user}
            crews={crews}
            crewId={crewId}
            setCrewId={setCrewId}
          />
        }
      />
      <Route
        path="/crews"
        element={
          <CrewManager
            crews={crews}
            crewMembers={crewMembers[crewId] || []}
            currentCrew={crewId}
            onSelect={setCrewId}
            onCreate={create}
            onJoin={join}
            onLeave={leave}
            reload={reload}
            userId={user?.id}
            entries={crewEntries}
            onOpenCashbook={setCbCrew}
            loadMembers={loadMembers}
          />
        }
      />
      <Route path="/cashbook/:crewId" element={<CashbookWrapper />} />
      <Route path="/cashbook" element={<CashbookView crewId={cbCrew} onBack={() => {}} />} />

      <Route
        path="/quick-mark"
        element={<QuickMarkRoute />}
      />
      <Route
        path="/donate"
        element={
          <PfandspendeForm
            user={user}
            onSubmitted={(created) => {
              if (created?.link) {
                window.location.href = created.link;
              } else {
                window.location.href = '/pfand_spende/';
              }
            }}
          />
        }
      />
      <Route
        path="/scanner"
        element={
          <BarcodeScanner
            onDetected={(code) => {
              setBarcode(code);
              setShowScanner(false);
              window.location.hash = '#/search';
            }}
            onClose={() => setShowScanner(false)}
          />
        }
      />
      <Route path="/profil/:username" element={<PublicProfileWrapper />} />
    </Routes>
  );
}

export default AppRoutes;
